import React, { useEffect, useRef } from 'react';
import { Typography, Box, IconButton, Grid, InputAdornment } from '@mui/material';
import { Add, Close as CloseIcon, Remove } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import modifyScriptName from '../../../../utils/modifyScriptName';
import axiosInstance from '../../../../utils/axios';
import {
  getUserPortfolio,
  cancelTrade,
  startTradeLoading,
  getTradeHistory,
  stopTradeLoading,
} from '../../../../redux/slices/trades';
import { Wrapper } from '../watchlist/BuyScriptDrawer';
import { RHFTextField, FormProvider, RHFToggleButtonGroup, RHFCheckbox } from '../../../../components/hook-form/index';
import useAuth from '../../../../hooks/useAuth';
import { fNumber } from '../../../../utils/formatNumber';

const CloseTradeModal = ({
  trade,
  isOpen,
  onClose,
  closeFromTrade = false,
  closeFromPortfolio = false,
  onUpdateSuccess,
  isManualTrade,
}) => {
  const isLoading = useSelector((state) => state?.trade?.isLoading);
  const quote = useSelector((state) => state?.trade?.quotesData[trade?.script?.instrument_token || trade?.script?.tv_token]);
  const { user } = useAuth();

  // Determine the initial action type based on trade type
  const initialActionType = trade.type === 'OPEN' ? 'close' : 'cancel';

  const schema = yup.object().shape({
    quantity: yup
      .number()
      .required('Quantity is required')
      .typeError('Quantity must be a number')
      .positive('Quantity must be greater than zero')
      .when('orderActionType', {
        is: 'close',
        then: yup.number().max(trade.quantity, `Quantity cannot exceed ${trade.quantity}`),
        otherwise: yup.number(), // No max validation for SL/Target
      }),
    tradeAction: yup.string().required('Action is required'), // For PENDING trades: 'cancel' or 'modify'
    orderActionType: yup
      .string()
      .when('trade.type', {
        // For OPEN trades: 'close', 'sl', or 'target'
        is: 'OPEN',
        then: yup.string().required('Order action type is required'),
        otherwise: yup.string().notRequired(),
      }),
    price: yup
      .number()
      .typeError('Price must be a number')
      .positive('Price must be greater than zero')
      .when(['useCustomPrice', 'tradeAction', 'orderActionType', 'trade.type'], {
        is: (useCustomPrice, tradeAction, orderActionType, tradeType) =>
          (tradeType === 'OPEN' && (orderActionType === 'sl' || orderActionType === 'target')) || // Required for SL and Target orders
          (tradeType === 'OPEN' && orderActionType === 'close' && useCustomPrice) || // Required for custom price close
          (tradeType === 'PENDING' && tradeAction === 'modify'), // Required for modify pending
        then: yup.number().required('Price is required'),
        otherwise: yup.number().notRequired(),
      })
      .when(['orderActionType'], {
        is: (orderActionType) => orderActionType === 'sl' && trade.side === 'BUY', // SL for BUY side
        then: () =>
          yup
            .number()
            .max(
              trade.buy,
              `Stop Loss price cannot be greater than the buy price (${trade.buy})`
            )
            .test(
              'sl-above-bid',
              `Stop Loss price must be below buy price (${trade.buy}) and below current Bid price (${quote?.depth?.buy?.[0]?.price || 'N/A'}).`,
              (value) => {
                // If no quote data available, skip this validation
                if (!quote || !quote.depth || !quote.depth.buy || !quote.depth.buy[0]) return true;
                const currentBidPrice = quote.depth.buy[0].price;

                // Check if price is higher than buy price - this would be invalid for SL
                if (value > trade.buy) {
                  return false;
                }

                // Check if price is ABOVE current Bid price - this would be invalid for SL
                if (value > currentBidPrice) {
                  return false;
                }

                // Check if price is equal to bid price - this would be invalid for SL
                if (value === currentBidPrice) {
                  return false;
                }

                return true;
              }
            ),
        otherwise: yup.number(),
      })
      .when(['orderActionType'], {
        is: (orderActionType) => orderActionType === 'sl' && trade.side === 'SELL',
        then: yup
          .number()
          .min(
            trade.sell,
            `Stop Loss price cannot be lower than the sell price (${trade.sell})`
          )
          .test(
            'sl-below-ask',
            'Stop Loss price must be above sell price and below current Ask price.',
            (value) => {
              // If no quote data available, skip this validation
              if (!quote || !quote.depth || !quote.depth.sell || !quote.depth.sell[0]) return true;
              const currentAskPrice = quote.depth.sell[0].price;

              // Check if price is lower than sell price - this would be invalid for SL
              if (value < trade.sell) {
                return false;
              }

              // Check if price is above ask price - this would be invalid for SL
              if (value >= currentAskPrice) {
                return false;
              }

              return true;
            }
          ),
        otherwise: yup.number(),
      })
      .when(['orderActionType'], {
        is: (orderActionType) => orderActionType === 'target' && trade.side === 'BUY', // Target for BUY side
        then: () =>
          yup
            .number()
            .min(trade.buy, `Target price cannot be lower than the buy price (${trade.buy})`)
            .test('target-for-buy', 'Target price must be above buy price.', (value) => {
              // If no quote data available, skip this validation
              if (!quote || !quote.depth || !quote.depth.sell || !quote.depth.sell[0]) return true;

              // Check if price is lower than buy price - this would be invalid for Target
              if (value < trade.buy) {
                return false;
              }

              return true;
            }),
        otherwise: yup.number(),
      })
      .when(['orderActionType'], {
        is: (orderActionType) => orderActionType === 'target' && trade.side === 'SELL',
        then: yup
          .number()
          .max(trade.sell, `Target price cannot be greater than the sell price (${trade.sell})`)
          .test('target-for-sell', 'Target price must be below sell price.', (value) => {
            // If no quote data available, skip this validation
            if (!quote || !quote.depth || !quote.depth.buy || !quote.depth.buy[0]) return true;

            // Check if price is higher than sell price - this would be invalid for Target
            if (value > trade.sell) {
              return false;
            }

            return true;
          }),
        otherwise: yup.number(),
      }),
    useCustomPrice: yup.boolean(),
  });

  const methods = useForm({
    mode: 'onChange', // Validate on change to provide immediate feedback
    reValidateMode: 'onChange', // Re-validate when the user makes changes
    resolver: yupResolver(schema),
    defaultValues: {
      quantity: trade.quantity,
      tradeAction: initialActionType, // 'cancel' for PENDING
      orderActionType: 'close', // Default for OPEN
      useCustomPrice: false,
      price: trade.side === 'BUY' ? trade.buy : trade.sell, // Initial price suggestion
    },
  });

  const { handleSubmit, reset, watch, setValue, trigger, formState } = methods; // Added formState to access validation errors
  const { errors, isValid, isDirty } = formState;
  const selectedOption = watch('tradeAction'); // For PENDING: 'cancel' or 'modify'
  const orderActionType = watch('orderActionType'); // For OPEN: 'close', 'sl', or 'target'
  const useCustomPrice = watch('useCustomPrice');

  // Helper function to get appropriate label for price field
  const getFieldLabel = () => {
    if (orderActionType === 'sl') return 'Stop Loss Price';
    if (orderActionType === 'target') return 'Target Price';
    return 'Price';
  };

  // Helper function to get button label based on action type
  const getButtonLabel = () => {
    if (orderActionType === 'sl') return 'Place Stop Loss Order';
    if (orderActionType === 'target') return 'Place Target Order';
    return 'Close Trade';
  };

  // Add trade.type to dependency array for schema reactivity
  const tradeType = trade.type;
  useEffect(() => {
    trigger(); // Re-trigger validation when trade type changes
  }, [tradeType, trigger, quote]); // Added quote to re-validate when market prices change

  useEffect(() => {
    // Reset the form with appropriate defaults when the modal opens or the trade changes
    if (isOpen) {
      reset({
        quantity: trade.quantity,
        // Set initial action based on trade type when modal opens
        tradeAction: trade.type === 'OPEN' ? 'close' : 'cancel',
        orderActionType: 'close', // Always default to 'close' when opening for OPEN trades
        price: parseFloat(parseFloat(trade.side === 'BUY' ? trade.buy : trade.sell).toFixed(2)),
        useCustomPrice: false,
      });

      // Validate the form after resetting to ensure button state is correct
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [isOpen, trade.type, trade.quantity, trade.buy, trade.sell, trade.side, reset, trigger]); // Dependencies: isOpen, trade, reset, trigger

  useEffect(() => {
    // Reset price and custom price flag when switching between sl/target/close or toggling custom price for OPEN trades
    // Also reset price for PENDING modify action
    if (trade.type === 'OPEN') {
      if (orderActionType === 'sl' || orderActionType === 'target') {
        setValue('price', 0); // Reset price for sl/target order input
        setValue('useCustomPrice', false); // Ensure custom price is off for sl/target
      } else if (orderActionType === 'close') {
        if (useCustomPrice) {
          setValue('price', 0); // Reset price for custom input
        }
        // No price reset needed if switching back to non-custom close
      }
    } else if (trade.type === 'PENDING') {
      if (selectedOption === 'modify') {
        // Set initial price for modify, could also reset to 0 if preferred
        setValue('price', trade.side === 'BUY' ? trade.buy : trade.sell);
      }
    }
  }, [orderActionType, useCustomPrice, selectedOption, setValue, trade.type, trade.side, trade.buy, trade.sell]);

  // Use refs to track if we've already set the SL, Target, and Modify prices
  const slPriceSetRef = useRef(false);
  const targetPriceSetRef = useRef(false);
  const modifyPriceSetRef = useRef(false);

  // Reset the refs when modal opens/closes or when action type changes
  useEffect(() => {
    if (!isOpen) {
      slPriceSetRef.current = false;
      targetPriceSetRef.current = false;
      modifyPriceSetRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    slPriceSetRef.current = false;
    targetPriceSetRef.current = false;
  }, [orderActionType]);

  // Reset the modify price ref when the selectedOption changes
  useEffect(() => {
    modifyPriceSetRef.current = false;
  }, [selectedOption]);

  useEffect(() => {
    // When we have live quotes and a relevant orderActionType is selected
    if (quote && quote.depth && isOpen) {
      // Special handling for Stop Loss - set price only once
      if (trade.type === 'OPEN' && orderActionType === 'sl' && !slPriceSetRef.current) {
        // For Stop Loss, use appropriate price based on trade side
        if (trade.side === 'BUY') {
          // For BUY trades, SL should be below buy price
          if (quote.depth.buy && quote.depth.buy[0]) {
            const currentBid = quote.depth.buy[0].price;
            // Calculate a suitable SL - 1% below buy price
            const suggestedSL = Math.min(currentBid - 0.01, trade.buy * 0.99);
            // Format to 2 decimal places and ensure it's not higher than buy price
            const formattedSL = parseFloat((suggestedSL < trade.buy ? suggestedSL : trade.buy * 0.99).toFixed(2));
            setValue('price', formattedSL);
            slPriceSetRef.current = true;
          }
        } else if (trade.side === 'SELL') {
          // For SELL trades, SL should be above sell price but below current Ask
          if (quote.depth.sell && quote.depth.sell[0]) {
            const currentAsk = quote.depth.sell[0].price;
            // Calculate a suitable SL - 1% above sell price but below current ask
            const suggestedSL = Math.min(currentAsk - 0.01, trade.sell * 1.01);
            // Format to 2 decimal places
            const formattedSL = parseFloat((suggestedSL > trade.sell ? suggestedSL : trade.sell * 1.01).toFixed(2));
            setValue('price', formattedSL);
            slPriceSetRef.current = true;
          }
        }
      }
      // Special handling for Target - set price only once
      else if (trade.type === 'OPEN' && orderActionType === 'target' && !targetPriceSetRef.current) {
        // For Target, use appropriate price based on trade side
        if (trade.side === 'BUY') {
          // For BUY trades, target should be above buy price
          // Calculate a suitable target - 2% above buy price
          const suggestedTarget = trade.buy * 1.02;
          // Format to 2 decimal places
          const formattedTarget = parseFloat(suggestedTarget.toFixed(2));
          setValue('price', formattedTarget);
          targetPriceSetRef.current = true;
        } else if (trade.side === 'SELL') {
          // For SELL trades, target should be below sell price
          if (quote.depth.buy && quote.depth.buy[0]) {
            // Calculate a suitable target - 2% below sell price
            const suggestedTarget = trade.sell * 0.98;
            // Format to 2 decimal places
            const formattedTarget = parseFloat(suggestedTarget.toFixed(2));
            setValue('price', formattedTarget);
            targetPriceSetRef.current = true;
          }
        }
      }
      // For custom price close/modify, allow updating with quote
      else if (trade.type === 'OPEN' && orderActionType === 'close' && useCustomPrice) {
        // Always use the bid price when available
        if (quote.depth.buy && quote.depth.buy[0]) {
          setValue('price', quote.depth.buy[0].price);
        }
      }
      // Special handling for PENDING 'modify' - set price only once
      else if (trade.type === 'PENDING' && selectedOption === 'modify' && !modifyPriceSetRef.current) {
        // For modify, use appropriate price based on depth data
        if (quote.depth.buy && quote.depth.buy[0]) {
          setValue('price', quote.depth.buy[0].price);
          modifyPriceSetRef.current = true; // Mark that we've set the price
        }
      }
    }
  }, [quote, orderActionType, trade.type, trade.side, useCustomPrice, selectedOption, isOpen, setValue]);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const handleFinalize = (isSuccess = true) => {
    if (isSuccess) {
      if (closeFromPortfolio && !isManualTrade) {
        dispatch(getUserPortfolio());
      }
      if (closeFromTrade && !isManualTrade) {
        dispatch(getTradeHistory());
      }
      if (isManualTrade) {
        onUpdateSuccess();
      }
    }
    if (isManualTrade || !isSuccess) {
      // Stop loading for manual trades or on error
      dispatch(stopTradeLoading());
    }
    onClose();
  };

  const handleCloseTrade = async (data) => {
    dispatch(startTradeLoading());
    let response;
    console.log('Closing trade from:', closeFromPortfolio ? 'Portfolio' : 'Trade History');
    try {
      const payload = { ...data };
      // Remove fields not needed for close API
      delete payload.orderActionType;
      delete payload.tradeAction;

      if (closeFromPortfolio) {
        payload.side = trade.side === 'BUY' ? 'SELL' : 'BUY';
        payload.orderType = 'MARKET'; // Default close is MARKET unless useCustomPrice
        payload.scriptId = trade.script._id;
        payload.cancelFromPortfolio = true;
        if (payload.useCustomPrice && payload.price > 0) {
          payload.orderType = 'LIMIT'; // Close with custom price becomes a LIMIT order
        } else {
          delete payload.price; // Don't send price for MARKET close
        }
        response = await axiosInstance.post('/trade/add', payload);
      } else if (closeFromTrade) {
        if (payload.useCustomPrice && payload.price > 0) {
          payload.orderType = 'LIMIT';
        } else {
          payload.orderType = 'MARKET';
          delete payload.price;
        }
        response = await axiosInstance.put(`/trade/close-trade/${trade._id}`, payload);
      }
      enqueueSnackbar(response.data.message);
      if (response.data.tradesCancelled) {
        enqueueSnackbar(response.data.cancelMessage, { persist: true });
      }
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
      handleFinalize(false);
    }
  };

  const handleCancelTrade = async () => {
    // Existing cancel logic remains the same
    dispatch(startTradeLoading()); // Start loading for cancel too
    try {
      const res = await dispatch(cancelTrade({ tradeId: trade._id, isManualTrade }));
      enqueueSnackbar(res.message);
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
      handleFinalize(false); // Ensure loading stops on error
    }
  };

  const handleModifyOrder = async (data) => {
    // Existing modify logic remains the same
    dispatch(startTradeLoading());
    try {
      const payload = { quantity: data.quantity, price: data.price }; // Only send relevant fields
      const response = await axiosInstance.put(`/trade/modify/limit/${trade._id}`, payload);
      enqueueSnackbar(response.data.message);
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message || 'Limit Between Bid/Ask is not allowed', { variant: 'error' });
      handleFinalize(false);
    }
  };

  // Handle SL order
  const handleSLOrder = async (data) => {
    dispatch(startTradeLoading());
    console.log('Placing SL order from:', closeFromPortfolio ? 'Portfolio' : 'Trade History');
    try {
      const payload = {
        quantity: data.quantity,
        limit: data.price,
        side: trade.side === 'BUY' ? 'SELL' : 'BUY', // Opposite side for the SL order
        orderType: 'LIMIT',
        scriptId: trade.script._id,
      };

      if (closeFromTrade) {
        payload.tradeId = trade._id;
      }

      // Add extra details for non-client users
      if (user.role.rolename !== 'client') {
        payload.price = data.price;
        payload.userId = trade.traderId;
        payload.force = true;
      }

      // Use different endpoint based on user role
      const endpoint = user.role.rolename !== 'client' ? '/trade/manual/add' : '/trade/add';
      const response = await axiosInstance.post(endpoint, payload);

      enqueueSnackbar(response.data.message);
      if (response.data.tradesCancelled) {
        enqueueSnackbar(response.data.cancelMessage, { persist: true });
      }
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
      handleFinalize(false);
    }
  };

  // Handle Target order
  const handleTargetOrder = async (data) => {
    dispatch(startTradeLoading());
    console.log('Placing Target order from:', closeFromPortfolio ? 'Portfolio' : 'Trade History');
    try {
      const payload = {
        quantity: data.quantity,
        limit: data.price,
        side: trade.side === 'BUY' ? 'SELL' : 'BUY', // Opposite side for the Target order
        orderType: 'LIMIT',
        scriptId: trade.script._id,
      };

      if (closeFromTrade) {
        payload.tradeId = trade._id;
      }

      // Add extra details for non-client users
      if (user.role.rolename !== 'client') {
        payload.price = data.price;
        payload.traderId = trade.traderId;
        payload.force = true;
      }

      // Use different endpoint based on user role
      const endpoint = user.role.rolename !== 'client' ? '/trade/manual/add' : '/trade/add';
      const response = await axiosInstance.post(endpoint, payload);

      enqueueSnackbar(response.data.message);
      if (response.data.tradesCancelled) {
        enqueueSnackbar(response.data.cancelMessage, { persist: true });
      }
      handleFinalize(true);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
      handleFinalize(false);
    }
  };

  // Determine the correct submit handler based on trade type and selected actions
  const getSubmitHandler = () => {
    if (trade.type === 'OPEN') {
      if (orderActionType === 'sl') {
        return handleSLOrder;
      }
      if (orderActionType === 'target') {
        return handleTargetOrder;
      }
      return handleCloseTrade;
    }
    if (trade.type === 'PENDING') {
      // For PENDING, the button click directly calls the handler,
      // but we still need a submit handler for the form if 'modify' is chosen.
      return selectedOption === 'modify' ? handleModifyOrder : () => {}; // No-op for cancel via submit
    }
    return () => console.error('Invalid state for submission'); // Fallback
  };

  return (
    <Wrapper
      open={isOpen}
      onClose={onClose}
      children={
        <Box width={{ xs: '100%', sm: '350px' }} marginX={'auto'}>
          <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" textAlign="center" pb={2}>
            {modifyScriptName(trade.script.tradingsymbol)}
          </Typography>
          {user.role.rolename === 'admin' && trade.type === 'OPEN' && (
            <Box py={2}>
              <Typography textAlign={'center'}>
                {trade.side === 'BUY' ? 'Buy' : 'Sell'} price: {trade.side === 'BUY' ? fNumber(trade.buy) : fNumber(trade.sell)}
              </Typography>
            </Box>
          )}
          {/* Pass trade.type to FormProvider if needed by children, though schema handles it */}
          <FormProvider methods={methods} onSubmit={handleSubmit(getSubmitHandler())}>
            {/* Action Selection: PENDING */}
            {trade.type === 'PENDING' && (
              <RHFToggleButtonGroup
                name="tradeAction" // Controls 'cancel' vs 'modify'
                label="Select Action"
                color="primary"
                // defaultValue is handled by useForm and the useEffect reset
                sx={{ width: '100%', mb: 2 }}
                options={[
                  { label: 'Cancel', value: 'cancel' },
                  { label: 'Modify', value: 'modify' },
                ]}
                exclusive // Ensure only one can be selected
              />
            )}
            {/* Action Selection: OPEN */}
            {trade.type === 'OPEN' && (
              <RHFToggleButtonGroup
                name="orderActionType" // Controls 'close', 'sl', or 'target'
                label="Select Action"
                color="primary"
                // defaultValue is handled by useForm and the useEffect reset
                sx={{ width: '100%', mb: 2 }}
                options={[
                  { label: 'Close', value: 'close' },
                  { label: 'SL', value: 'sl' },
                  { label: 'Target', value: 'target' },
                ]}
                exclusive // Ensure only one can be selected
              />
            )}

            <Box sx={{ display: 'grid', gap: 2 }}>
              {/* Quantity Field: Visible for OPEN trades, or PENDING 'modify' */}
              {(trade.type === 'OPEN' || (trade.type === 'PENDING' && selectedOption === 'modify')) && (
                <RHFTextField
                  name="quantity"
                  label="Quantity"
                  type="number"
                  step={['COMEX', 'CRYPTO', 'NSEIX', 'WINDEX'].includes(trade?.script?.exchange?.name) ? '0.10' : '1'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onClick={() => {
                            const step = ['COMEX', 'CRYPTO', 'NSEIX', 'WINDEX'].includes(trade?.script?.exchange?.name) ? 0.1 : 1;
                            const currentValue = Number((methods.getValues().quantity || 0).toFixed(2));
                            methods.setValue('quantity', Number((currentValue - step).toFixed(2)));
                          }}
                        >
                          <Remove />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            const step = ['COMEX', 'CRYPTO', 'NSEIX', 'WINDEX'].includes(trade?.script?.exchange?.name) ? 0.1 : 1;
                            const currentValue = Number((methods.getValues().quantity || 0).toFixed(2));
                            methods.setValue('quantity', Number((currentValue + step).toFixed(2)));
                          }}
                        >
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Custom Price Checkbox: Visible for OPEN 'close' action by admin */}
              {(user.role.rolename === 'admin' || user.role.rolename === 'superAdmin') &&
                trade.type === 'OPEN' &&
                orderActionType === 'close' && <RHFCheckbox name={'useCustomPrice'} label="Close with Custom Price" />}
              {/* Price Field: Visible for PENDING 'modify', OPEN 'sl', OPEN 'target', or OPEN 'close' with custom price */}
              {(trade.type === 'PENDING' && selectedOption === 'modify') ||
              (trade.type === 'OPEN' && (orderActionType === 'sl' || orderActionType === 'target')) ||
              (trade.type === 'OPEN' && orderActionType === 'close' && useCustomPrice) ? (
                <>
                  <RHFTextField
                    name="price"
                    label={getFieldLabel()}
                    type="number"
                    InputProps={{
                      readOnly: false, // Make all price fields editable
                    }}
                  />
                  {/* Display bid/ask information from quote data */}
                  {quote && quote.depth && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', px: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Bid: {quote.depth.buy && quote.depth.buy[0] ? fNumber(quote.depth.buy[0].price) : 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ask: {quote.depth.sell && quote.depth.sell[0] ? fNumber(quote.depth.sell[0].price) : 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : null}
            </Box>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              {/* Buttons for OPEN trades */}
              {trade.type === 'OPEN' && (
                <Grid item xs={12}>
                  <LoadingButton
                    type="submit" // Submit triggers the handler determined by getSubmitHandler
                    variant="contained"
                    fullWidth
                    size="large"
                    color={orderActionType === 'close' ? 'error' : 'primary'} // Red for close, Blue for SL/Target
                    loading={isLoading}
                    disabled={
                      (orderActionType === 'sl' || orderActionType === 'target') &&
                      (!isValid || Object.keys(errors).length > 0)
                    }
                    sx={{ textTransform: 'uppercase' }}
                  >
                    {getButtonLabel()}
                  </LoadingButton>
                </Grid>
              )}

              {/* Buttons for PENDING trades */}
              {trade.type === 'PENDING' && (
                <>
                  {selectedOption === 'cancel' && (
                    <Grid item xs={12}>
                      <LoadingButton
                        variant="contained"
                        onClick={handleCancelTrade} // Cancel doesn't need form data, direct call
                        fullWidth
                        size="large"
                        color="error"
                        loading={isLoading}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Cancel Order
                      </LoadingButton>
                    </Grid>
                  )}
                  {selectedOption === 'modify' && (
                    <Grid item xs={12}>
                      <LoadingButton
                        type="submit" // Modify uses form data via submit
                        variant="contained"
                        fullWidth
                        size="large"
                        color="primary"
                        loading={isLoading}
                        disabled={!isValid || Object.keys(errors).length > 0}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Modify Order
                      </LoadingButton>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </FormProvider>
        </Box>
      }
    />
  );
};

CloseTradeModal.propTypes = {
  trade: PropTypes.object,
  isOpen: PropTypes.bool,
  closeFromPortfolio: PropTypes.bool,
  closeFromTrade: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdateSuccess: PropTypes.func,
  isManualTrade: PropTypes.bool,
};

export default CloseTradeModal;
